/**
 * Adventure Category Types and Node Permissions
 * Defines category metadata, node type groupings, and category-specific restrictions
 */

import { AdventureMainCategory } from './adventure-editor.types'
import { WorkflowNodeType, WorkflowConditionType } from './workflow.types'

// ====================
// CATEGORY METADATA
// ====================

/**
 * Metadata for an adventure category including display properties and features
 */
export interface CategoryMetadata {
  id: AdventureMainCategory
  name: string
  icon: string                    // MUI icon name
  color: string                   // Hex color
  description: string
  features: string[]              // Feature highlights for this category
}

/**
 * Complete category metadata for all adventure types
 */
export const ADVENTURE_CATEGORIES: CategoryMetadata[] = [
  {
    id: AdventureMainCategory.TraditionalGeoCaches,
    name: 'Traditional Geocache',
    icon: 'Explore',
    color: '#4CAF50',
    description: 'Classic treasure hunting with GPS coordinates',
    features: ['GPS coordinates', 'Physical container', 'Logbook signing', 'Trackable items'],
  },
  {
    id: AdventureMainCategory.NatureAdventure,
    name: 'Nature Adventure',
    icon: 'Nature',
    color: '#8BC34A',
    description: 'Explore natural environments and wildlife',
    features: ['Trail waypoints', 'Wildlife spotting', 'Photo opportunities', 'Educational content'],
  },
  {
    id: AdventureMainCategory.HistoricalAdventure,
    name: 'Historical Adventure',
    icon: 'HistoryEdu',
    color: '#795548',
    description: 'Discover history and heritage sites',
    features: ['Historical facts', 'Landmark tours', 'Timeline events', 'Photo archives'],
  },
  {
    id: AdventureMainCategory.UrbanExploration,
    name: 'Urban Exploration',
    icon: 'LocationCity',
    color: '#607D8B',
    description: 'Navigate city streets and discover urban gems',
    features: ['City navigation', 'Local businesses', 'Street art', 'Architecture'],
  },
  {
    id: AdventureMainCategory.RiddleQuest,
    name: 'Riddle Quest',
    icon: 'Quiz',
    color: '#9C27B0',
    description: 'Solve puzzles and follow clues',
    features: ['Puzzles & riddles', 'Clue sequences', 'Logic challenges', 'Mystery storylines'],
  },
  {
    id: AdventureMainCategory.PlaceOfInterest,
    name: 'Place of Interest',
    icon: 'Place',
    color: '#FF5722',
    description: 'Detailed information about a specific location',
    features: ['Single location', 'Detailed info', 'Visitor tips', 'Reviews & ratings'],
  },
  {
    id: AdventureMainCategory.VirtualTour,
    name: 'Virtual Tour',
    icon: 'Vrpano',
    color: '#2196F3',
    description: 'Experience locations remotely',
    features: ['360° views', 'Remote access', 'Multimedia content', 'Self-paced exploration'],
  },
  {
    id: AdventureMainCategory.Trails,
    name: 'Trail',
    icon: 'Route',
    color: '#FF9800',
    description: 'Follow a path with timed checkpoints',
    features: ['Timed completion', 'Waypoint checkpoints', 'Leaderboard ranking', 'Trailcraft rating'],
  },
  {
    id: AdventureMainCategory.SilentAdventure,
    name: 'Silent Adventure',
    icon: 'VisibilityOff',
    color: '#9E9E9E',
    description: 'Auto-completing adventure without workflow',
    features: ['Silent join', 'Silent complete', 'Duration-based', 'No user interaction required'],
  },
]

/**
 * Get category metadata by ID
 */
export function getCategoryMetadata(categoryId: AdventureMainCategory | number): CategoryMetadata | undefined {
  return ADVENTURE_CATEGORIES.find(c => c.id === categoryId)
}

/**
 * Get category color by ID
 */
export function getCategoryColor(categoryId: AdventureMainCategory | number): string {
  return getCategoryMetadata(categoryId)?.color ?? '#757575'
}

/**
 * Get category icon by ID
 */
export function getCategoryIcon(categoryId: AdventureMainCategory | number): string {
  return getCategoryMetadata(categoryId)?.icon ?? 'HelpOutline'
}

// ====================
// NODE TYPE DEFINITIONS
// ====================

/**
 * Node type definition with metadata
 */
export interface NodeTypeDefinition {
  type: WorkflowNodeType
  name: string
  description: string
}

/**
 * Node type groups organized by category
 */
export type NodeTypeGroup = 'control' | 'challenges' | 'rewards' | 'journey' | 'exits'

/**
 * All node types organized by group
 */
export const NODE_TYPES: Record<NodeTypeGroup, NodeTypeDefinition[]> = {
  control: [
    { type: WorkflowNodeType.Start, name: 'Start', description: 'Entry point for workflow' },
    { type: WorkflowNodeType.Decision, name: 'Decision', description: 'Logical conditions (weather, time, geofence, etc.)' },
    { type: WorkflowNodeType.Loop, name: 'Loop', description: 'Repeats a section with configurable cooldown' },
    { type: WorkflowNodeType.Delay, name: 'Delay', description: 'Pauses before continuing (seconds to days)' },
  ],
  challenges: [
    { type: WorkflowNodeType.QuestionAnswer, name: 'QuestionAnswer', description: 'Free text, multiple choice, or multi-select Q&A' },
    { type: WorkflowNodeType.QRCode, name: 'QRCode', description: 'QR code scanner with custom code fallback' },
    { type: WorkflowNodeType.NumericInput, name: 'NumericInput', description: 'Number pad with 2-6 configurable digits' },
    { type: WorkflowNodeType.ModeratorApproval, name: 'ModeratorApproval', description: 'Requires moderator approval to continue' },
  ],
  rewards: [
    { type: WorkflowNodeType.PoiLocation, name: 'PoiLocation', description: 'Map location with emoji markers or circle overlays' },
    { type: WorkflowNodeType.TextClue, name: 'TextClue', description: 'Message to adventure journal' },
    { type: WorkflowNodeType.AudioPlayer, name: 'AudioPlayer', description: 'Audio playback (MP3, AAC, WAV, OGG, Opus)' },
    { type: WorkflowNodeType.VideoPlayer, name: 'VideoPlayer', description: 'Video playback (YouTube, Vimeo, Dailymotion, Twitch)' },
    { type: WorkflowNodeType.ImageDisplay, name: 'ImageDisplay', description: 'Image display with optional caption' },
    { type: WorkflowNodeType.UnlockAdventure, name: 'UnlockAdventure', description: 'Unlock another adventure' },
    { type: WorkflowNodeType.TimeModifier, name: 'TimeModifier', description: 'Add time bonus or penalty' },
    { type: WorkflowNodeType.FormattedMessage, name: 'FormattedMessage', description: 'Rich text content from editor' },
    { type: WorkflowNodeType.GroupMembership, name: 'GroupMembership', description: 'Add user to a group' },
    { type: WorkflowNodeType.GiftReward, name: 'GiftReward', description: 'Distribute gift rewards' },
  ],
  journey: [
    { type: WorkflowNodeType.Route, name: 'Route', description: 'Display route between locations (walking/cycling/driving)' },
  ],
  exits: [
    { type: WorkflowNodeType.Exit, name: 'Exit', description: 'Generic exit' },
    { type: WorkflowNodeType.ExitSuccess, name: 'ExitSuccess', description: 'Adventure completed successfully' },
    { type: WorkflowNodeType.ExitFailed, name: 'ExitFailed', description: 'Adventure failed' },
    { type: WorkflowNodeType.ExitAbandoned, name: 'ExitAbandoned', description: 'Adventure abandoned' },
  ],
}

/**
 * Get all node types as a flat array
 */
export function getAllNodeTypes(): NodeTypeDefinition[] {
  return Object.values(NODE_TYPES).flat()
}

/**
 * Get node types for a specific group
 */
export function getNodeTypesByGroup(group: NodeTypeGroup): NodeTypeDefinition[] {
  return NODE_TYPES[group]
}

/**
 * Get node type definition by type
 */
export function getNodeTypeDefinition(nodeType: WorkflowNodeType): NodeTypeDefinition | undefined {
  return getAllNodeTypes().find(n => n.type === nodeType)
}

/**
 * Challenge node types
 */
export const CHALLENGE_NODE_TYPES: WorkflowNodeType[] = [
  WorkflowNodeType.QuestionAnswer,
  WorkflowNodeType.QRCode,
  WorkflowNodeType.NumericInput,
  WorkflowNodeType.ModeratorApproval,
]

/**
 * Check if a node type is a challenge type
 */
export function isChallengeNodeType(nodeType: WorkflowNodeType): boolean {
  return CHALLENGE_NODE_TYPES.includes(nodeType)
}

/**
 * Reward node types
 */
export const REWARD_NODE_TYPES: WorkflowNodeType[] = [
  WorkflowNodeType.PoiLocation,
  WorkflowNodeType.TextClue,
  WorkflowNodeType.AudioPlayer,
  WorkflowNodeType.VideoPlayer,
  WorkflowNodeType.ImageDisplay,
  WorkflowNodeType.UnlockAdventure,
  WorkflowNodeType.TimeModifier,
  WorkflowNodeType.FormattedMessage,
  WorkflowNodeType.GroupMembership,
  WorkflowNodeType.GiftReward,
]

/**
 * Check if a node type is a reward type
 */
export function isRewardNodeType(nodeType: WorkflowNodeType): boolean {
  return REWARD_NODE_TYPES.includes(nodeType)
}

/**
 * Exit node types
 */
export const EXIT_NODE_TYPES: WorkflowNodeType[] = [
  WorkflowNodeType.Exit,
  WorkflowNodeType.ExitSuccess,
  WorkflowNodeType.ExitFailed,
  WorkflowNodeType.ExitAbandoned,
]

/**
 * Check if a node type is an exit type
 */
export function isExitNodeType(nodeType: WorkflowNodeType): boolean {
  return EXIT_NODE_TYPES.includes(nodeType)
}

// ====================
// CATEGORY RESTRICTIONS
// ====================

/**
 * Fixed restrictions for geocache adventures (cannot be changed by creator)
 */
export interface GeocacheRestrictions {
  isPublic: true
  allowGroups: false
  isRepeatable: false
}

/**
 * Special features for trail adventures
 */
export interface TrailSpecialFeatures {
  hasLeaderboard: true
  ratingCategories: readonly ['trailcraft']
}

/**
 * Node permissions for a category
 */
export interface CategoryNodePermissions {
  categoryName: string
  workflowAllowed: boolean
  workflowRequired: boolean
  allowedNodeTypes: NodeTypeGroup[] | 'all'
  allowedChallengeTypes: string[]       // Challenge type names (empty for no challenges)
  restrictions: 'configurable' | GeocacheRestrictions
  allowedCriteriaTypes: WorkflowConditionType[] | 'all'
  specialFeatures?: TrailSpecialFeatures
  notes?: string
}

/**
 * Node permissions indexed by category ID
 */
export const NODE_PERMISSIONS_BY_CATEGORY: Record<number, CategoryNodePermissions> = {
  [AdventureMainCategory.TraditionalGeoCaches]: {
    categoryName: 'Traditional Geocache',
    workflowAllowed: false,
    workflowRequired: false,
    allowedNodeTypes: [],
    allowedChallengeTypes: [],
    restrictions: {
      isPublic: true,
      allowGroups: false,
      isRepeatable: false,
    },
    allowedCriteriaTypes: [
      WorkflowConditionType.TimeSchedule,
      WorkflowConditionType.Geofence,
      WorkflowConditionType.AdventureCollectionCompletion,
      WorkflowConditionType.AdventureCompleted,
      WorkflowConditionType.WeatherDayNight,
    ],
    notes: 'Geocaches do NOT allow node flows at all. Limited to basic criteria only.',
  },
  [AdventureMainCategory.NatureAdventure]: {
    categoryName: 'Nature Adventure',
    workflowAllowed: true,
    workflowRequired: false,
    allowedNodeTypes: 'all',
    allowedChallengeTypes: ['QuestionAnswer', 'QRCode', 'NumericInput', 'ModeratorApproval'],
    restrictions: 'configurable',
    allowedCriteriaTypes: 'all',
  },
  [AdventureMainCategory.HistoricalAdventure]: {
    categoryName: 'Historical Adventure',
    workflowAllowed: true,
    workflowRequired: false,
    allowedNodeTypes: 'all',
    allowedChallengeTypes: ['QuestionAnswer', 'QRCode', 'NumericInput', 'ModeratorApproval'],
    restrictions: 'configurable',
    allowedCriteriaTypes: 'all',
  },
  [AdventureMainCategory.UrbanExploration]: {
    categoryName: 'Urban Exploration',
    workflowAllowed: true,
    workflowRequired: false,
    allowedNodeTypes: 'all',
    allowedChallengeTypes: ['QuestionAnswer', 'QRCode', 'NumericInput', 'ModeratorApproval'],
    restrictions: 'configurable',
    allowedCriteriaTypes: 'all',
  },
  [AdventureMainCategory.RiddleQuest]: {
    categoryName: 'Riddle Quest',
    workflowAllowed: true,
    workflowRequired: false,
    allowedNodeTypes: 'all',
    allowedChallengeTypes: ['QuestionAnswer', 'QRCode', 'NumericInput', 'ModeratorApproval'],
    restrictions: 'configurable',
    allowedCriteriaTypes: 'all',
  },
  [AdventureMainCategory.PlaceOfInterest]: {
    categoryName: 'Place of Interest',
    workflowAllowed: true,
    workflowRequired: false,
    allowedNodeTypes: 'all',
    allowedChallengeTypes: ['QuestionAnswer', 'QRCode', 'NumericInput', 'ModeratorApproval'],
    restrictions: 'configurable',
    allowedCriteriaTypes: 'all',
  },
  [AdventureMainCategory.VirtualTour]: {
    categoryName: 'Virtual Tour',
    workflowAllowed: true,
    workflowRequired: false,
    allowedNodeTypes: 'all',
    allowedChallengeTypes: ['QuestionAnswer', 'QRCode', 'NumericInput', 'ModeratorApproval'],
    restrictions: 'configurable',
    allowedCriteriaTypes: 'all',
  },
  [AdventureMainCategory.Trails]: {
    categoryName: 'Trail',
    workflowAllowed: true,
    workflowRequired: true,
    allowedNodeTypes: ['control', 'rewards', 'journey', 'exits'],
    allowedChallengeTypes: [],
    restrictions: 'configurable',
    allowedCriteriaTypes: 'all',
    specialFeatures: {
      hasLeaderboard: true,
      ratingCategories: ['trailcraft'] as const,
    },
    notes: 'Trails REQUIRE workflow but CANNOT use challenge nodes. Must have at least one waypoint/checkpoint.',
  },
  [AdventureMainCategory.SilentAdventure]: {
    categoryName: 'Silent Adventure',
    workflowAllowed: false,
    workflowRequired: false,
    allowedNodeTypes: [],
    allowedChallengeTypes: [],
    restrictions: 'configurable',
    allowedCriteriaTypes: 'all',
    notes: 'Silent adventures do NOT allow workflow nodes. Auto-completes based on duration or silent join.',
  },
}

// ====================
// PERMISSION HELPERS
// ====================

/**
 * Get node permissions for a category
 */
export function getCategoryPermissions(categoryId: AdventureMainCategory | number): CategoryNodePermissions | undefined {
  return NODE_PERMISSIONS_BY_CATEGORY[categoryId]
}

/**
 * Check if workflow is allowed for a category
 */
export function isWorkflowAllowed(categoryId: AdventureMainCategory | number): boolean {
  const permissions = getCategoryPermissions(categoryId)
  return permissions?.workflowAllowed ?? true
}

/**
 * Check if workflow is required for a category
 */
export function isWorkflowRequired(categoryId: AdventureMainCategory | number): boolean {
  const permissions = getCategoryPermissions(categoryId)
  return permissions?.workflowRequired ?? false
}

/**
 * Check if a node type is allowed for a category
 */
export function isNodeTypeAllowed(categoryId: AdventureMainCategory | number, nodeType: WorkflowNodeType): boolean {
  const permissions = getCategoryPermissions(categoryId)
  if (!permissions) return true

  // Workflow not allowed at all
  if (!permissions.workflowAllowed) return false

  // All node types allowed
  if (permissions.allowedNodeTypes === 'all') return true

  // Check if the node's group is in the allowed list
  const nodeGroups = permissions.allowedNodeTypes as NodeTypeGroup[]

  // Find which group this node type belongs to
  for (const group of nodeGroups) {
    const groupNodes = NODE_TYPES[group]
    if (groupNodes.some(n => n.type === nodeType)) {
      return true
    }
  }

  // Special check for challenges - explicitly disallowed for some categories
  if (isChallengeNodeType(nodeType) && permissions.allowedChallengeTypes.length === 0) {
    return false
  }

  return false
}

/**
 * Check if challenges are allowed for a category
 */
export function areChallengesAllowed(categoryId: AdventureMainCategory | number): boolean {
  const permissions = getCategoryPermissions(categoryId)
  if (!permissions) return true
  return permissions.allowedChallengeTypes.length > 0
}

/**
 * Check if a specific challenge type is allowed for a category
 */
export function isChallengeTypeAllowed(categoryId: AdventureMainCategory | number, challengeName: string): boolean {
  const permissions = getCategoryPermissions(categoryId)
  if (!permissions) return true
  return permissions.allowedChallengeTypes.includes(challengeName)
}

/**
 * Get allowed node types for a category
 */
export function getAllowedNodeTypes(categoryId: AdventureMainCategory | number): WorkflowNodeType[] {
  const permissions = getCategoryPermissions(categoryId)
  if (!permissions || !permissions.workflowAllowed) return []

  if (permissions.allowedNodeTypes === 'all') {
    return getAllNodeTypes().map(n => n.type)
  }

  const allowedGroups = permissions.allowedNodeTypes as NodeTypeGroup[]
  const nodeTypes: WorkflowNodeType[] = []

  for (const group of allowedGroups) {
    nodeTypes.push(...NODE_TYPES[group].map(n => n.type))
  }

  return nodeTypes
}

/**
 * Get allowed criteria types for a category
 */
export function getAllowedCriteriaTypes(categoryId: AdventureMainCategory | number): WorkflowConditionType[] | 'all' {
  const permissions = getCategoryPermissions(categoryId)
  if (!permissions) return 'all'
  return permissions.allowedCriteriaTypes
}

/**
 * Check if a criteria type is allowed for a category
 */
export function isCriteriaTypeAllowed(categoryId: AdventureMainCategory | number, criteriaType: WorkflowConditionType): boolean {
  const allowed = getAllowedCriteriaTypes(categoryId)
  if (allowed === 'all') return true
  return allowed.includes(criteriaType)
}

/**
 * Check if category has fixed restrictions (like geocaches)
 */
export function hasFixedRestrictions(categoryId: AdventureMainCategory | number): boolean {
  const permissions = getCategoryPermissions(categoryId)
  return permissions?.restrictions !== 'configurable'
}

/**
 * Get fixed restrictions for a category (if any)
 */
export function getFixedRestrictions(categoryId: AdventureMainCategory | number): GeocacheRestrictions | null {
  const permissions = getCategoryPermissions(categoryId)
  if (permissions?.restrictions === 'configurable') return null
  return permissions?.restrictions ?? null
}

/**
 * Check if category has a leaderboard
 */
export function hasLeaderboard(categoryId: AdventureMainCategory | number): boolean {
  const permissions = getCategoryPermissions(categoryId)
  return permissions?.specialFeatures?.hasLeaderboard ?? false
}

/**
 * Get rating categories for an adventure category
 */
export function getRatingCategories(categoryId: AdventureMainCategory | number): readonly string[] {
  const permissions = getCategoryPermissions(categoryId)
  return permissions?.specialFeatures?.ratingCategories ?? []
}
